import { Box, Stack } from '@mui/material';
import { FC, Fragment } from 'react';

import Close from '@vp/assets/icons/Close.svg?react';
import MagicWandFilled from '@vp/assets/icons/MagicWandFilled.svg?react';
import { VpButton } from '@vp/common/ui/component/VpButton';
import { VpDialog } from '@vp/common/ui/component/VpDialog';
import { VpHeader } from '@vp/common/ui/component/VpHeader';
import { VpIconButton } from '@vp/common/ui/component/VpIconButton';
import { VpImage } from '@vp/common/ui/component/VpImage';
import { VpMediaGradientOverlay } from '@vp/common/ui/component/VpMediaGradientOverlay';
import { useViewModel } from '@vp/common/ui/hook/useViewModel';
import { VpPlayer } from '@vp/common/ui/player/VpPlayer';
import { GalleryManagerSection } from '@vp/manager/gallery/ui/GalleryManagerSection';
import { GalleryManagerPreviewViewModel } from '@vp/manager/gallery/ui/preview/GalleryManagerPreviewViewModel';

export interface PhotoPreview {
  id: string;
  url: string;
  hash: string;
}

export interface VideoPreview {
  id: string;
  url: string;
  thumbnail: string;
  thumbnailHash: string;
}

export interface GalleryManagerPreviewProps {
  item: PhotoPreview | VideoPreview;
  section: GalleryManagerSection;
  close: () => void;
}

export const GalleryManagerPreview: FC<GalleryManagerPreviewProps> = props => {
  const viewModel = useViewModel(GalleryManagerPreviewViewModel, props);

  return (
    <Fragment>
      <Stack width={1} height={1}>
        <VpHeader sx={{ px: { tablet: 4 }, pt: { tablet: 2 } }}>
          <VpIconButton
            disabled={viewModel.removing.value}
            sx={theme => ({ [theme.breakpoints.up('desktop')]: { p: 1 } })}
            Icon={Close}
            onClick={props.close}
          />
        </VpHeader>

        <VpMediaGradientOverlay sx={{ display: 'flex', flexGrow: 1 }}>
          {viewModel.isPhotoPreview(props.item) && (
            <VpImage src={viewModel.currentPhoto.value!.url} hash={viewModel.currentPhoto.value!.hash} mode="contain" />
          )}

          {!viewModel.isPhotoPreview(props.item) && (
            <VpPlayer url={props.item.url} thumbnail={props.item.thumbnail} thumbnailHash={props.item.thumbnailHash} sx={{ flexGrow: 1 }} />
          )}
        </VpMediaGradientOverlay>

        <Box
          display="flex"
          justifyContent="center"
          width={1}
          px={{ mobile: 3, tablet: 7, desktop: 0 }}
          pb={{ mobile: 2, tablet: 4 }}
          pt={{ mobile: 2, tablet: 3 }}
        >
          <Stack direction="row" alignItems="center" maxWidth={650} width={1} gap={1}>
            <VpButton text="Удалить" status="primary" variant="soft" size="small" sx={{ flex: 1 }} onClick={viewModel.showDeleteDialog} />

            {viewModel.showAnimatePhotoButton.value && (
              <VpButton
                text={viewModel.animateButtonText.value}
                status="primary"
                variant="solid"
                size="small"
                sx={{ flex: 1 }}
                disabled={viewModel.animateButtonDisabled.value}
                loading={viewModel.animateButtonLoading.value}
                Icon={MagicWandFilled}
                onClick={viewModel.onAnimateButtonClick}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      <VpDialog
        maxWidth={450}
        open={viewModel.deleteDialogShown.value}
        title={`Удалить ${viewModel.sectionName.value}`}
        text={`Вы уверены, что хотите удалить ${viewModel.sectionName.value}? Это действие нельзя отменить.`}
        actionText="Удалить"
        cancelText="Отменить"
        actionLoading={viewModel.removing.value}
        cancelDisabled={viewModel.removing.value}
        onAction={viewModel.remove}
        onCancel={viewModel.closeDeleteDialog}
      />

      <VpDialog
        maxWidth={450}
        open={viewModel.animateDialogShown.value}
        title={`Анимировать фото`}
        text={viewModel.animateDialogText.value}
        actionText="Анимировать"
        cancelText="Отменить"
        actionLoading={viewModel.animating.value}
        cancelDisabled={viewModel.animating.value}
        onAction={viewModel.animate}
        onCancel={viewModel.closeAnimateDialog}
      />

      <VpDialog
        maxWidth={450}
        open={viewModel.makeStaticDialogShown.value}
        title={`Вернуть изначальное фото`}
        text={
          'Анимация будет отменена, и фото вернётся к исходному виду. При повторной анимации лимит не уменьшится, так как анимированное фото уже сохранено.'
        }
        actionText="Вернуть"
        cancelText="Отменить"
        actionLoading={viewModel.makingStatic.value}
        cancelDisabled={viewModel.makingStatic.value}
        onAction={viewModel.makeStatic}
        onCancel={viewModel.closeMakeStaticDialog}
      />
    </Fragment>
  );
};
